type SideScoped = { sideID: number };
type ItemArea = SideScoped & { [key: string]: unknown };
type TemplateItem = {
    itemID?: number;
    itemGuid?: string;
    syncGuid?: string | null;
    areas: ItemArea[];
    [key: string]: unknown;
};
type TemplateDesignData = {
    designID: number;
    modelID: number;
    isTemplate: boolean;
    designItems: TemplateItem[];
    sides?: SideScoped[];
    clientPreviews?: SideScoped[];
    cameraScreenshots?: SideScoped[];
    [key: string]: unknown;
};

const isObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const uniqueSideEntries = <T extends SideScoped>(entries: T[]) => Array.from(new Map(entries.map((entry) => [JSON.stringify(entry), entry])).values());

/** Merge the iframe's one-side draft without replacing unrelated areas of the shared product template. */
export const mergeComposerTemplateAreaDraft = (
    currentDesign: MPlaza.Design,
    incoming: unknown,
    sideID: number,
    updateSettings: boolean,
    itemIdentityMap = new Map<string, string>()
): MPlaza.Design => {
    const saved = currentDesign.toJSON() as TemplateDesignData;
    if (
        !isObject(incoming) ||
        !Number.isSafeInteger(sideID) ||
        sideID <= 0 ||
        incoming.designID !== saved.designID ||
        incoming.modelID !== saved.modelID ||
        incoming.isTemplate !== true ||
        saved.isTemplate !== true ||
        !Array.isArray(incoming.designItems) ||
        incoming.designItems.some(
            (item) =>
                !isObject(item) ||
                !Array.isArray(item.areas) ||
                item.areas.length === 0 ||
                item.areas.some((area: unknown) => !isObject(area) || area.sideID !== sideID)
        )
    ) {
        throw new Error('The Full Editor sent an invalid or out-of-scope template draft. Reopen the area and try again.');
    }
    const draft = incoming as TemplateDesignData;
    const sideCollections = ['sides', 'sidesPrintTypes', 'areasGrids', 'areas', 'clientPreviews', 'cameraScreenshots', 'canvasRotation', 'options'];
    for (const name of sideCollections) {
        const values = draft[name];
        if (values !== undefined && (!Array.isArray(values) || values.some((value) => !isObject(value) || (value.sideID ?? value.SideID) !== sideID))) {
            throw new Error('The Full Editor draft contains another area and cannot be applied.');
        }
    }

    const merged: TemplateDesignData = { ...saved };
    if (updateSettings) {
        if (!draft.sides?.some((side) => side.sideID === sideID)) throw new Error('The edited area restrictions are missing.');
        // This editor owns only its area. Product/template metadata stays in Composer.
        merged.sides = [...(saved.sides ?? []).filter((side) => side.sideID !== sideID), ...draft.sides];
    }
    for (const name of ['clientPreviews', 'cameraScreenshots'] as const) {
        const next = draft[name];
        if (next?.length) merged[name] = uniqueSideEntries([...(saved[name] ?? []).filter((entry) => entry.sideID !== sideID), ...next]);
    }

    const preservedItems: TemplateItem[] = [];
    for (const item of saved.designItems ?? []) {
        const otherAreas = (item.areas ?? []).filter((area) => area.sideID !== sideID);
        if (otherAreas.length) preservedItems.push({ ...item, areas: otherAreas });
    }
    const usedGuids = new Set(preservedItems.map((item) => item.itemGuid?.toLowerCase()).filter(Boolean));
    const incomingGuids = new Set<string>();
    for (const item of draft.designItems) {
        const itemGuid = item.itemGuid;
        if (!itemGuid || incomingGuids.has(itemGuid.toLowerCase())) throw new Error('The Full Editor draft has duplicate or missing item IDs.');
        incomingGuids.add(itemGuid.toLowerCase());
        const mappedGuid = itemIdentityMap.get(itemGuid.toLowerCase()) ?? itemGuid;
        const next: TemplateItem & { itemGuid: string } = { ...item, itemGuid: mappedGuid };
        if (usedGuids.has(mappedGuid.toLowerCase())) {
            next.itemID = -1;
            next.itemGuid = MPlaza.generateUUID();
            next.syncGuid = null;
            itemIdentityMap.set(itemGuid.toLowerCase(), next.itemGuid);
        } else if (mappedGuid !== itemGuid) {
            next.itemID = -1;
            next.syncGuid = null;
        }
        usedGuids.add(next.itemGuid.toLowerCase());
        preservedItems.push(next);
    }
    merged.designItems = preservedItems;
    return new MPlaza.Design(merged);
};
